/**
 * Calendar event day bounds (aligned with mobile CalendarScreen / getEventDayBounds).
 * Multi-day events span every calendar day from start through end (local dates).
 */
import moment from 'moment';

/**
 * @param {{ start_time: string, end_time?: string | null }} ev
 * @returns {{ start: moment.Moment, end: moment.Moment } | null}
 */
export function getEventDayBounds(ev) {
    const start = moment(ev.start_time).startOf('day');
    if (!start.isValid()) {
        return null;
    }
    if (!ev.end_time) {
        return { start, end: start.clone() };
    }
    const end = moment(ev.end_time).startOf('day');
    if (!end.isValid()) {
        return { start, end: start.clone() };
    }
    if (end.isBefore(start, 'day')) {
        return { start, end: start.clone() };
    }
    return { start, end };
}

/**
 * @param {moment.Moment} start
 * @param {moment.Moment} end
 * @returns {string[]} YYYY-MM-DD
 */
export function eachDayInRange(start, end) {
    const out = [];
    let d = start.clone();
    while (d.isBefore(end, 'day') || d.isSame(end, 'day')) {
        out.push(d.format('YYYY-MM-DD'));
        d.add(1, 'day');
    }
    return out;
}

/**
 * @param {Array} events
 * @param {string} currentDateStr YYYY-MM-DD
 * @param {'day' | 'prev' | 'next'} mode — same semantics as mobile CalendarScreen
 */
export function filterEventsByCalendarMode(events, currentDateStr, mode) {
    if (!events || events.length === 0) {
        return [];
    }
    const cur = moment(currentDateStr).startOf('day');
    if (!cur.isValid()) {
        return [];
    }
    const res = [];
    events.forEach((ev) => {
        const bounds = getEventDayBounds(ev);
        if (!bounds) {
            return;
        }
        const { start, end } = bounds;
        if (mode === 'day') {
            if (
                (cur.isSame(start, 'day') || cur.isAfter(start, 'day')) &&
                (cur.isSame(end, 'day') || cur.isBefore(end, 'day'))
            ) {
                res.push(ev);
            }
        } else if (mode === 'prev') {
            if (end.isBefore(cur, 'day')) {
                res.push(ev);
            }
        } else if (mode === 'next') {
            if (start.isAfter(cur, 'day')) {
                res.push(ev);
            }
        }
    });
    return res.slice().reverse();
}

/**
 * Dates that have at least one event (for calendar day markers).
 * @param {Array} events
 * @returns {Set<string>}
 */
export function buildMarkedDateSet(events) {
    const set = new Set();
    if (!events || events.length === 0) {
        return set;
    }
    events.forEach((ev) => {
        const bounds = getEventDayBounds(ev);
        if (!bounds) {
            return;
        }
        const { start, end } = bounds;
        eachDayInRange(start, end).forEach((d) => set.add(d));
    });
    return set;
}

/**
 * API may return an array or a map of id → event.
 * @param {unknown} payload
 * @returns {Array}
 */
export function normalizeEventsResponse(payload) {
    if (payload == null) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload.filter(Boolean);
    }
    if (typeof payload === 'object') {
        return Object.values(payload).filter(Boolean);
    }
    return [];
}
